import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { doc, getDoc, addDoc, collection, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
// Note: role display should prioritize the user's assignment role for the active CMT
import { VideoCameraOutlined, SettingOutlined } from '@ant-design/icons';
import { createIncidentLog, ACTIVITY_TYPES } from '../utils/activityLogger';
import { useNavigate } from 'react-router-dom';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import CMTSelector from './navigation/CMTSelector';
import { getAutomationManager } from '../services/automationManager';

const PageHeader = ({ title, extra, incident }) => {
  const { currentUser, logout } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  const { activeCmt, isMultiCmtEnabled, getUserRoleInCMT } = useMultiCmt();
  const [organisationInfo, setOrganisationInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check if user is a CMT member for the incident's team
  const isCMTMember = () => {
    if (!currentUser) return false;

    // 🎯 MODERNIZED: Check via activeCmt from MultiCmtContext
    if (incident?.cmtId) {
      // If user has access to the incident's CMT via MultiCmtContext, they have access
      return activeCmt?.id === incident.cmtId;
    }

    // Fallbacks (legacy/admin)
    const cmtRoles = [
      'cmt_leader',
      'cmt_admin',
      'cmt_member',
      'crisis_manager',
      'safety_officer',
      'comms_media',
      'legal_compliance'
    ];

    return currentUser.isAdmin ||
           cmtRoles.includes(currentUser.role) ||
           (incident?.cmtMemberIds && Array.isArray(incident.cmtMemberIds) && incident.cmtMemberIds.includes(currentUser?.uid));
  };

  // Slugify function to create URL-safe room names
  const slugify = (text) => {
    if (!text) return 'default-room';
    
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
      .trim()
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  // Handle video call button click with API integration
  const handleJoinVideoCall = async () => {
    if (!incident?.title || !incident?.id) {
      console.warn('No incident data available for video call');
      return;
    }

    try {
      // Call our backend to ensure room exists
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/dailyRoomManager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          incidentId: incident.id,
          incidentTitle: incident.title,
          action: 'ensure'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.roomUrl) {
        // Log room access before opening
        try {
          await createIncidentLog({
            incidentId: incident.id,
            orgId: currentUser.orgId,
            action: 'video_call_room_accessed',
            details: `${currentUser.displayName || currentUser.email} accessed video conference room "${result.roomName}" for incident "${incident.title}"`,
            userId: currentUser.uid,
            userEmail: currentUser.email,
            userFullName: currentUser.displayName || currentUser.email,
            priority: 'normal',
            metadata: {
              roomName: result.roomName,
              roomUrl: result.roomUrl,
              roomAction: result.action,
              userRole: currentUser.role,
              accessMethod: 'join_video_call_button'
            }
          });
        } catch (logError) {
          console.error('Error logging video room access:', logError);
          // Don't prevent room access if logging fails
        }

        // Open the video room in new window and track when user leaves
        const videoWindow = window.open(result.roomUrl, "_blank", "noopener,noreferrer,width=1200,height=800");

        // SURGICAL: Post Live Updates system message and create automation notification with dedupe
        try {
          const joinUrl = result.roomUrl;
          const meetingId = result.roomName || result.roomUrl;
          const dedupeKey = `${incident.id}:${meetingId}`;
          const orgId = currentUser.orgId;

          // Deduplicate recent notifications for same incident+meeting
          const notifRef = collection(db, 'c3_notifications');
          const qNotif = query(
            notifRef,
            where('orgId', '==', orgId),
            where('incidentId', '==', incident.id),
            where('type', '==', 'video_meeting'),
            where('dedupeKey', '==', dedupeKey)
          );
          const existing = await getDocs(qNotif);
          if (existing.empty) {
            // Live Updates system message
            await addDoc(collection(db, 'c3_incidentChats'), {
              incidentId: incident.id,
              orgId,
              message: `${currentUser.displayName || currentUser.email} has started a video conference. ${joinUrl}`,
              timestamp: serverTimestamp(),
              authorId: 'system',
              authorName: 'Video Conference System',
              role: 'system'
            });

            // Banner notification: client attempt (may be skipped by perms)
            try {
              const automation = getAutomationManager(db);
              await automation.createCustomNotification({
                orgId,
                incidentId: incident.id,
                type: 'video_meeting',
                priority: 'important',
                targetRole: 'all',
                title: 'Video call started',
                message: `${currentUser.displayName || currentUser.email} started a video call`,
                actionRequired: true,
                joinUrl,
                meetingId,
                dedupeKey,
                targetCmtId: (incident.cmtId || null)
              });
            } catch (_) {}

            // Banner notification: server-side callable (no CORS)
            try {
              const { getFunctions, httpsCallable } = await import('firebase/functions');
              const functions = getFunctions(undefined, 'australia-southeast2');
              const callNotify = httpsCallable(functions, 'notifyVideoMeetingCallable');
              await callNotify({
                orgId,
                incidentId: incident.id,
                targetCmtId: (incident.cmtId || null),
                joinUrl,
                meetingId,
                dedupeKey,
                message: `${currentUser.displayName || currentUser.email} started a video call`
              });
            } catch (srvErr) {
              console.warn('notifyVideoMeeting failed (non-blocking):', srvErr);
            }
          }
        } catch (notifyErr) {
          console.error('Error creating video meeting notification:', notifyErr);
        }
        
        // Track when user closes the video call window
        if (videoWindow) {
          const checkWindowClosed = () => {
            if (videoWindow.closed) {
              // Log when user leaves the video call
              createIncidentLog({
                incidentId: incident.id,
                orgId: currentUser.orgId,
                action: 'video_call_participant_left',
                details: `${currentUser.displayName || currentUser.email} left video conference room "${result.roomName}" for incident "${incident.title}"`,
                userId: currentUser.uid,
                userEmail: currentUser.email,
                userFullName: currentUser.displayName || currentUser.email,
                priority: 'normal',
                metadata: {
                  roomName: result.roomName,
                  roomUrl: result.roomUrl,
                  userRole: currentUser.role,
                  leaveMethod: 'window_closed',
                  leftAt: new Date().toISOString()
                }
              }).catch(error => {
                console.error('Error logging video call leave:', error);
              });
              
              console.log(`User left video room: ${result.roomName}`);
              return; // Stop checking
            }
            
            // Check again in 3 seconds
            setTimeout(checkWindowClosed, 3000);
          };
          
          // Start monitoring after a short delay
          setTimeout(checkWindowClosed, 5000);
        }
        
        console.log(`Video room ${result.action}: ${result.roomName}`);
      } else {
        throw new Error(result.error || 'Failed to get room URL');
      }

    } catch (error) {
      console.error('Error launching video call:', error);
      
      // Fallback to simple room creation
      const incidentSlug = slugify(incident?.title) || 'default-room';
      const fallbackURL = `https://crisiscompass.daily.co/${incidentSlug}`;
      
      // Log fallback room access
      try {
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: 'video_call_room_accessed',
          details: `${currentUser.displayName || currentUser.email} accessed video conference room "${incidentSlug}" for incident "${incident.title}" (fallback method due to API error)`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: currentUser.displayName || currentUser.email,
          priority: 'normal',
          metadata: {
            roomName: incidentSlug,
            roomUrl: fallbackURL,
            roomAction: 'fallback_access',
            userRole: currentUser.role,
            accessMethod: 'fallback_url',
            apiError: error.message
          }
        });
      } catch (logError) {
        console.error('Error logging fallback video room access:', logError);
      }

      const fallbackWindow = window.open(fallbackURL, "_blank", "noopener,noreferrer,width=1200,height=800");
      
      // Track when user closes the fallback video call window
      if (fallbackWindow) {
        const checkFallbackWindowClosed = () => {
          if (fallbackWindow.closed) {
            // Log when user leaves the fallback video call
            createIncidentLog({
              incidentId: incident.id,
              orgId: currentUser.orgId,
              action: 'video_call_participant_left',
              details: `${currentUser.displayName || currentUser.email} left video conference room "${incidentSlug}" for incident "${incident.title}" (fallback room)`,
              userId: currentUser.uid,
              userEmail: currentUser.email,
              userFullName: currentUser.displayName || currentUser.email,
              priority: 'normal',
              metadata: {
                roomName: incidentSlug,
                roomUrl: fallbackURL,
                userRole: currentUser.role,
                leaveMethod: 'fallback_window_closed',
                leftAt: new Date().toISOString()
              }
            }).catch(error => {
              console.error('Error logging fallback video call leave:', error);
            });
            
            console.log(`User left fallback video room: ${incidentSlug}`);
            return; // Stop checking
          }
          
          // Check again in 3 seconds
          setTimeout(checkFallbackWindowClosed, 3000);
        };
        
        // Start monitoring after a short delay
        setTimeout(checkFallbackWindowClosed, 5000);
      }
      
      // Show user-friendly error message
      console.warn('Using fallback room creation due to API error');
    }
  };

  // Fetch organisation information
  useEffect(() => {
    const fetchOrganisation = async () => {
      if (currentUser?.orgId && db) {
        try {
          setLoading(true);
          const orgDoc = await getDoc(doc(db, 'c3_organisations', currentUser.orgId));
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            setOrganisationInfo({
              name: orgData.name || 'Unknown Organisation',
              companyName: orgData.companyName,
              id: orgDoc.id
            });
          } else {
            setOrganisationInfo({
              name: 'Organisation Not Found',
              id: currentUser.orgId
            });
          }
        } catch (error) {
          console.error('Error fetching organisation:', error);
          setOrganisationInfo({
            name: 'Error Loading Organisation',
            id: currentUser.orgId
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOrganisation();
  }, [currentUser?.orgId, db]);

  // Get user's display role
  const getUserRole = () => {
    if (!currentUser) return null;

    // 🎯 MODERNIZED: Get role from MultiCmtContext
    let roleFromAssignment = null;

    if (activeCmt?.id) {
      // 🔧 SURGICAL FIX: Get actual role from MultiCmtContext instead of hardcoding 'member'
      roleFromAssignment = getUserRoleInCMT(activeCmt.id);
    }

    // Fallback to top-level user.role if no assignment found
    return roleFromAssignment || currentUser.role || null;
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      padding: '8px 16px',
      background: 'white',
      borderBottom: '1px solid #f0f0f0',
      marginBottom: '8px',
      position: 'relative'
    }}>
      <h1 style={{ 
        fontSize: '1.5rem',
        margin: 0,
        fontWeight: 500
      }}>{title}</h1>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        marginLeft: 'auto',
        paddingLeft: '100px' // Add space to prevent overlap with notification icons
      }}>
        {extra}
        
        {/* Video Call Button - Visible to anyone who can view the incident */}
        {incident && (
          <button
            onClick={handleJoinVideoCall}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 12px',
              backgroundColor: '#39325b',
              borderRadius: '6px',
              border: '1px solid #39325b',
              color: 'white',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2d2547';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#39325b';
            }}
          >
            <VideoCameraOutlined style={{ fontSize: '14px' }} />
            Join Video Call
          </button>
        )}
        
        {/* Organisation Info & User Role - Responsive Layout */}
        <div className="org-role-container">
          {/* Organisation Info */}
          {organisationInfo && (
            <div className="org-role-badge">
              <span className="org-role-label">ORG:</span>
              <span className="org-role-value org-name">
                {loading ? 'Loading...' : (organisationInfo.companyName || organisationInfo.name)}
              </span>
            </div>
          )}
          
          {/* CMT Selector - Only show if user has multiple CMTs */}
          {isMultiCmtEnabled && (
            <div className="org-role-badge" style={{ 
              padding: '4px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span className="org-role-label">CMT:</span>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CMTSelector 
                  size="small"
                  style={{ 
                    minWidth: '160px',
                    backgroundColor: 'transparent',
                    border: 'none'
                  }}
                  showRoles={false}
                  showMetadata={false}
                />
              </div>
            </div>
          )}
          
          {/* User Role (CMT Role) */}
          {getUserRole() && (
            <div className="org-role-badge">
              <span className="org-role-label">ROLE:</span>
              <span className="org-role-value">
                {getUserRole()}
              </span>
            </div>
          )}
        </div>
        
        {/* Settings Gear Icon - Only visible to CMT members */}
        {isCMTMember() && (
          <button
            onClick={() => navigate('/org-settings')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              backgroundColor: '#39325b',
              borderRadius: '6px',
              border: '1px solid #39325b',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2d2547';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#39325b';
            }}
            title="Organisation Settings - Configure SMS templates and team preferences"
          >
            <SettingOutlined style={{ fontSize: '16px' }} />
          </button>
        )}
        
        {/* User Email */}
        <span style={{ 
          color: '#666', 
          whiteSpace: 'nowrap',
          fontSize: '0.9rem'
        }}>
          {currentUser?.email}
        </span>
        
        <button 
          onClick={() => logout()} 
          style={{ 
            border: '1px solid #dc3545',
            color: '#dc3545',
            background: 'transparent',
            padding: '4px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default PageHeader; 